import { ProductVariantApi } from "../models/CatalogProducts/types";
import { OrderProductsVariantSchema } from "../models/Order/OrderProductsVariant";
import { ProductSourcingSkuStatusInfo } from "../models/ProjectManagement/ProjectPackagingSwagpacks";
import { PurhcaseRequestDetails } from "../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestProduct } from "../models/PurchaseRequest/PurchaseRequestProductInsights";
import {
  PurchaseRequestSkuVariant,
  PurchaseRequestSkuVariantRequestBody,
} from "../models/PurchaseRequest/PurchaseRequestProductRequestBody";

export const hasPurchaseRequestSkuVariants = (
  detail?: { sku_variants?: unknown[] } | null,
): boolean => Boolean(detail?.sku_variants?.length);

const normalizeSkuKey = (sku?: string | null): string =>
  (sku || "").trim().toLowerCase();

export const computeSkuInwardedQuantity = (
  row: Pick<
    ProductSourcingSkuStatusInfo,
    | "total_received_quantity"
    | "total_rejected_quantity"
    | "raw_received_quantity"
    | "raw_rejected_quantity"
  >,
): number =>
  Math.max(
    0,
    (Number(row.raw_received_quantity ?? row.total_received_quantity) || 0) -
      (Number(row.raw_rejected_quantity ?? row.total_rejected_quantity) || 0),
  );

/** True when sourcing-info-by-sku has at least one non-empty SKU for this product. */
export const hasProductSourcingSkus = (
  rows: ProductSourcingSkuStatusInfo[],
  productId: number,
): boolean =>
  rows.some((row) => row.product_id === productId && Boolean(row.sku?.trim()));

export const findSourcingSkuStatus = (
  rows: ProductSourcingSkuStatusInfo[],
  productId: number,
  variant?: { id?: number | null; sku?: string | null } | null,
): ProductSourcingSkuStatusInfo | null => {
  if (!variant || !hasProductSourcingSkus(rows, productId)) {
    return null;
  }

  const productSkuRows = rows.filter(
    (row) => row.product_id === productId && Boolean(row.sku?.trim()),
  );
  const skuKey = normalizeSkuKey(variant.sku);

  if (skuKey) {
    const bySku = productSkuRows.find(
      (row) => normalizeSkuKey(row.sku) === skuKey,
    );
    if (bySku) return bySku;
  }

  if (variant.id != null) {
    const byVariantId = productSkuRows.find(
      (row) =>
        row.variant_id != null &&
        Number(row.variant_id) === Number(variant.id),
    );
    if (byVariantId) return byVariantId;
  }

  return null;
};

export const getPurchaseRequestProductIds = (
  detail?: PurhcaseRequestDetails | null,
): number[] => {
  if (hasPurchaseRequestSkuVariants(detail)) {
    return Array.from(
      new Set(
        (detail!.sku_variants || [])
          .map((variant) => variant.product_id)
          .filter(Boolean),
      ),
    );
  }

  return Array.from(
    new Set(
      (detail?.products || [])
        .map((product) => product.product_id)
        .filter(Boolean),
    ),
  );
};

export const getPurchaseRequestLineItems = (
  detail?: PurhcaseRequestDetails | null,
): Array<{
  quantity?: number;
  rate?: number;
  cost_price?: number;
  gst_percentage?: number;
}> => {
  if (hasPurchaseRequestSkuVariants(detail)) {
    return detail!.sku_variants || [];
  }
  return detail?.products || [];
};

export interface PurchaseRequestSkuProductContext {
  product_name?: string;
  description?: string;
  gst_percentage?: number;
  hsn_code?: string | null;
  defaultCostPrice?: number;
  defaultSellingPrice?: number;
}

const toDateString = (value?: Date | string | null): string | undefined => {
  if (!value) return undefined;
  return typeof value === "string" ? value : value.toISOString();
};

export const enrichPurchaseRequestSkuVariant = (
  variant: PurchaseRequestSkuVariant,
  context: PurchaseRequestSkuProductContext = {},
): PurchaseRequestSkuVariant => {
  const costPrice = variant.cost_price ?? context.defaultCostPrice;

  return {
    ...variant,
    product_name: variant.product_name ?? context.product_name,
    cost_price: costPrice,
    rate: variant.rate ?? costPrice,
    selling_price: variant.selling_price ?? context.defaultSellingPrice,
    hsn_code: variant.hsn_code ?? context.hsn_code ?? null,
    gst_percentage: variant.gst_percentage ?? context.gst_percentage,
    description: variant.description ?? context.description,
  };
};

export const isPurchaseRequestSkuVariantSelected = (
  variant: PurchaseRequestSkuVariant,
): boolean => variant.selected === true;

export const getRemainingOrderVariantQuantity = (
  variant: Pick<OrderProductsVariantSchema, "quantity" | "ordered_quantity">,
): number => {
  const wanted = Number(variant.quantity || 0);
  const ordered = Number(variant.ordered_quantity ?? 0);
  return Math.max(0, wanted - ordered);
};

export const fromOrderProductsVariant = (
  variant: OrderProductsVariantSchema,
  context: PurchaseRequestSkuProductContext = {},
): PurchaseRequestSkuVariant =>
  enrichPurchaseRequestSkuVariant(
    {
      id: variant.id,
      title: variant.title || variant.sku,
      sku: variant.sku,
      quantity: getRemainingOrderVariantQuantity(variant),
      selected: false,
      created_by_admin_id: variant.created_by_admin_id ?? undefined,
      updated_by_admin_id: variant.updated_by_admin_id,
      created_date: toDateString(variant.created_at),
      modified_date: toDateString(variant.updated_at),
    },
    context,
  );

export const fromCatalogProductVariant = (
  variant: ProductVariantApi,
  context: PurchaseRequestSkuProductContext = {},
): PurchaseRequestSkuVariant =>
  enrichPurchaseRequestSkuVariant(
    {
      id: variant.id,
      title: variant.title || variant.name || variant.sku || `Variant #${variant.id}`,
      sku: variant.sku || "",
      cost_price: variant.cost_price ?? undefined,
      rate: variant.cost_price ?? undefined,
      selling_price: variant.selling_price ?? undefined,
      active: variant.active ? 1 : 0,
      selected: false,
    },
    context,
  );

export const mergePurchaseRequestSkuVariants = (
  source: PurchaseRequestSkuVariant[],
  current: PurchaseRequestSkuVariant[],
  context: PurchaseRequestSkuProductContext = {},
): PurchaseRequestSkuVariant[] => {
  const currentBySku = new Map(
    current.map((line) => [line.sku.trim().toLowerCase(), line]),
  );

  return source.map((line) => {
    const existing = currentBySku.get(line.sku.trim().toLowerCase());
    return enrichPurchaseRequestSkuVariant(
      {
        ...line,
        ...existing,
        quantity: existing?.quantity ?? line.quantity,
        cost_price: existing?.cost_price ?? line.cost_price ?? context.defaultCostPrice,
        rate: existing?.rate ?? existing?.cost_price ?? line.rate ?? line.cost_price,
        selling_price:
          existing?.selling_price ??
          line.selling_price ??
          context.defaultSellingPrice,
        hsn_code: existing?.hsn_code ?? line.hsn_code ?? context.hsn_code ?? null,
        description: existing?.description ?? line.description ?? context.description,
        gst_percentage:
          existing?.gst_percentage ?? line.gst_percentage ?? context.gst_percentage,
        selected:
          existing?.selected ?? line.selected ?? false,
      },
      context,
    );
  });
};

export const toPurchaseRequestSkuVariantRequestBody = (
  variant: PurchaseRequestSkuVariant,
  productId: number,
): PurchaseRequestSkuVariantRequestBody => {
  const costPrice = variant.cost_price;

  return {
    product_id: productId,
    id: variant.id,
    product_name: variant.product_name,
    title: variant.title,
    sku: variant.sku,
    quantity: variant.quantity,
    cost_price: costPrice,
    rate: variant.rate ?? costPrice,
    selling_price: variant.selling_price,
    hsn_code: variant.hsn_code ?? null,
    gst_percentage: variant.gst_percentage,
    description: variant.description,
    active: variant.active,
    created_by_admin_id: variant.created_by_admin_id,
    updated_by_admin_id: variant.updated_by_admin_id,
    created_date: variant.created_date,
    modified_date: variant.modified_date,
  };
};

export const isValidPurchaseRequestSkuVariant = (
  variant: PurchaseRequestSkuVariant,
): boolean =>
  isPurchaseRequestSkuVariantSelected(variant) &&
  Boolean(variant.sku) &&
  Number(variant.quantity) > 0 &&
  Number(variant.cost_price) > 0;

export const isValidStockIssueRequestSkuVariant = (
  variant: PurchaseRequestSkuVariant,
): boolean =>
  isPurchaseRequestSkuVariantSelected(variant) &&
  Boolean(variant.sku) &&
  Number(variant.quantity) > 0;

export interface InvoiceSelectableLine {
  key: string;
  productId: number;
  variantId: number | null;
  skuCode: string | null;
  title: string;
  productName: string;
  quantity: number;
  rate: number;
  gstPercentage: number;
  hsnCode?: string | null;
  isSkuVariant: boolean;
}

const getInvoiceLineKey = (
  productId: number,
  variantId?: number | null,
  skuCode?: string | null,
): string => {
  if (variantId) {
    return `sku:${productId}:${variantId}`;
  }
  if (skuCode) {
    return `sku:${productId}:${skuCode}`;
  }
  return `product:${productId}`;
};

export const buildInvoiceSelectableLines = (
  products: PurchaseRequestProduct[],
  skuVariants?: PurchaseRequestSkuVariantRequestBody[],
): InvoiceSelectableLine[] => {
  const lines: InvoiceSelectableLine[] = [];
  const variantsByProduct = new Map<number, PurchaseRequestSkuVariantRequestBody[]>();

  (skuVariants || []).forEach((variant) => {
    const productId = Number(variant.product_id);
    if (!productId) return;
    const hasSku =
      variant.variant_id != null || Boolean(String(variant.sku || "").trim());
    if (!hasSku) return;
    const list = variantsByProduct.get(productId) || [];
    list.push(variant);
    variantsByProduct.set(productId, list);
  });

  products.forEach((product) => {
    const productId = Number(product.product_id);
    const variants = variantsByProduct.get(productId) || [];
    if (variants.length > 0) {
      variants.forEach((variant) => {
        const rate =
          variant.rate != null
            ? Number(variant.rate)
            : variant.cost_price != null
              ? Number(variant.cost_price)
              : Number(product.rate) || 0;
        const gst =
          variant.gst_percentage != null
            ? Number(variant.gst_percentage)
            : Number(product.gst_percentage) || 0;

        lines.push({
          key: getInvoiceLineKey(
            productId,
            variant.variant_id,
            variant.sku,
          ),
          productId,
          variantId:
            variant.variant_id != null ? Number(variant.variant_id) : null,
          skuCode: variant.sku || null,
          title:
            variant.title?.trim() ||
            variant.sku ||
            `Variant #${variant.variant_id ?? variant.id}`,
          productName: product.product_name,
          quantity: Number(variant.quantity) || 0,
          rate,
          gstPercentage: gst,
          hsnCode: product.hsn_code,
          isSkuVariant: true,
        });
      });
      return;
    }

    lines.push({
      key: getInvoiceLineKey(productId),
      productId,
      variantId: null,
      skuCode: null,
      title: product.product_name,
      productName: product.product_name,
      quantity: Number(product.quantity) || 0,
      rate: Number(product.rate) || 0,
      gstPercentage: Number(product.gst_percentage) || 0,
      hsnCode: product.hsn_code,
      isSkuVariant: false,
    });
  });

  return lines;
};
